import { useEffect, useState } from "react";

import { DatePicker, useDatePickerHelpers } from "@newsela/angelou";
import PropTypes from "prop-types";

import AssignmentFieldHeader from "@/assignments/AssignmentFieldHeader";
import { useScheduleAssign } from "@/assignments/useScheduleAssign/useScheduleAssign";
import {
  popOutCss,
  labelHeaderCss,
  fieldCss,
  scheduleSectionCss,
  errorCss,
} from "./ScheduleInput.style";

const propTypes = {
  onChange: PropTypes.func,
  dateGoesLive: PropTypes.string,
  originalDateGoesLive: PropTypes.string,
  maxScheduleDate: PropTypes.string,
};

const ScheduleInput = ({
  onChange,
  dateGoesLive,
  originalDateGoesLive,
  maxScheduleDate,
}) => {
  const { getIsoDateValue } = useDatePickerHelpers();

  const { scheduledDateTime, minValue, resetDate } = useScheduleAssign({
    dateGoesLive,
    originalDateGoesLive,
  });
  const { parseAbsoluteToLocal } = useDatePickerHelpers();

  const [value, setValue] = useState(scheduledDateTime);
  const maxValueAttribute = maxScheduleDate
    ? { maxValue: parseAbsoluteToLocal(maxScheduleDate) }
    : {};

  useEffect(() => {
    /**
     * Set second and milliseconds to 0 since we are only validating in whole
     * minutes and since we are not allowing teachers to schedule to seconds
     * and milliseconds. Otherwise, a value of 2023-07-09T23:00:00.001Z will
     * display the error message if the content expiration date is
     * 2023-07-09T23:00:00.000Z.
     */
    const isoDateString =
      (value && getIsoDateValue(value.set({ second: 0, millisecond: 0 }))) ||
      "";

    if (value && value >= minValue && isoDateString) {
      // `value` in local state should always be Aria Date object however we store ISO String in Assignment Reducer / Assignments API
      onChange(isoDateString);
    }
  }, [value]);

  return (
    <div css={scheduleSectionCss}>
      <AssignmentFieldHeader>
        {originalDateGoesLive ? "Scheduled for" : "Schedule for later"}
      </AssignmentFieldHeader>
      <DatePicker
        dataProps={{ "data-qa-selector": "schedule-assignment-date-picker" }}
        __cssFor={{
          ...fieldCss,
          PopOut: popOutCss,
          dateFieldLabelContainer: {
            label: labelHeaderCss,
          },
          errorMessage: errorCss,
        }}
        calendarActionButton={{
          content: "Reset",
          onPress: () => setValue(resetDate),
          shouldCloseOnPress: false,
        }}
        label="Schedule for later"
        minValue={minValue?.set({ second: 0, millisecond: 0 })}
        {...maxValueAttribute}
        onChange={setValue}
        timeField={{
          label: "Time",
        }}
        value={value}
      />
    </div>
  );
};

ScheduleInput.propTypes = propTypes;

export default ScheduleInput;
